import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import EvolutieBarChart, { fmtMii } from "./EvolutieBarChart";
import { sel } from "./configuratorMetrics";

const GRANS = [["an", "Ani"], ["semestru", "Semestre"], ["trimestru", "Trimestre"], ["luna", "Luni"], ["saptamana", "Saptamani"], ["zi", "Zile"]];
const TIPURI = [["toti", "Toti"], ["pf", "Persoana fizica"], ["pj", "Persoana juridica"], ["nedefinit", "Nedefinit"]];
const SUBTABS = [["erp-nou", "ERP-nou"], ["erp-vechi", "ERP-vechi"], ["cumulat", "Cumulat"]];
const fmt = fmtMii;

export default function EvolutieComenzi() {
  const [sub, setSub] = useState("erp-nou");
  return (
    <div data-testid="evolutii-comenzi">
      <div className="mb-4 flex gap-1">
        {SUBTABS.map(([k, l]) => (
          <button key={k} data-testid={`evolutii-comenzi-subtab-${k}`} onClick={() => setSub(k)}
            className={`rounded px-3 py-1.5 text-sm font-medium ${sub === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {l}
          </button>
        ))}
      </div>
      <PanouComenzi key={sub} endpoint={`/api/comenzi-evolutie/${sub}`} testid={`evolutii-comenzi-${sub}`} />
    </div>
  );
}

function PanouComenzi({ endpoint, testid }) {
  const [f, setF] = useState({ date_from: "", date_to: "", granularity: "an", tip: "toti" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if ((f.date_from && !f.date_to) || (!f.date_from && f.date_to)) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(endpoint, { params: f });
      setData(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Eroare la incarcare");
    } finally {
      setLoading(false);
    }
  }, [f, endpoint]);

  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div data-testid={testid}>
      <Filtre f={f} set={set} testid={testid} />
      {loading && <div data-testid={`${testid}-loading`} className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Se incarca...</div>}
      {error && <div data-testid={`${testid}-error`} className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {data && !error && (
        <>
          <Sumar data={data} testid={testid} />
          <Grafice data={data} toti={f.tip === "toti"} testid={testid} />
          <Tabel rows={data.rows || []} totals={data.totals || {}} testid={testid} />
        </>
      )}
    </div>
  );
}

function Filtre({ f, set, testid }) {
  return (
    <div className="mb-5 flex flex-wrap items-center justify-end gap-3 text-sm">
      <label className="flex items-center gap-2">Tip client
        <select data-testid={`${testid}-filter-tip`} className={sel} value={f.tip} onChange={set("tip")}>
          {TIPURI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      <label className="flex items-center gap-2">Granularitate
        <select data-testid={`${testid}-filter-granularity`} className={sel} value={f.granularity} onChange={set("granularity")}>
          {GRANS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      <label className="flex items-center gap-2">De la
        <input data-testid={`${testid}-filter-date-from`} type="date" className={sel} value={f.date_from} onChange={set("date_from")} />
      </label>
      <label className="flex items-center gap-2">Pana la
        <input data-testid={`${testid}-filter-date-to`} type="date" className={sel} value={f.date_to} onChange={set("date_to")} />
      </label>
    </div>
  );
}

function Sumar({ data, testid }) {
  const t = data.totals || {};
  const items = [["total", "Total"], ["pf", "PF"], ["pj", "PJ"], ["nedefinit", "Nedefinit"]];
  return (
    <div data-testid={`${testid}-sumar`} className="mb-4 flex flex-wrap gap-3 text-sm">
      {items.map(([k, l]) => (
        <div key={k} data-testid={`${testid}-card-${k}`} className="rounded border border-slate-200 bg-white px-3 py-2 shadow-sm">
          <div className="text-slate-500">{l}</div>
          <div className="text-slate-800"><b>{fmt(t[k])}</b> comenzi</div>
          <div className="text-slate-800"><b>{fmt(t[`val_${k}`])}</b> EUR fara TVA</div>
        </div>
      ))}
      <div className="self-center text-xs text-slate-500">
        {data.date_from && <div>Interval: {data.date_from} - {data.date_to}</div>}
        <div>Valoare EUR fara TVA: /1.19 pana la 31.08.2025 inclusiv, /1.21 ulterior.</div>
      </div>
      {data.dedup && <Dedup d={data.dedup} testid={testid} />}
    </div>
  );
}

function Dedup({ d, testid }) {
  return (
    <div data-testid={`${testid}-dedup`} className="w-full text-xs text-slate-500">
      Surse: ERP-vechi {fmtMii(d.erp_vechi)} + ERP-nou {fmtMii(d.erp_nou)}. Comenzi unice: {fmtMii(d.unici)}. Dubluri eliminate: {fmtMii(d.dubluri_eliminate)} (legate prin mysql_id: {fmtMii(d.legate_mysql_id)}, prin nr. comanda: {fmtMii(d.legate_nr_comanda)}). Doar in ERP-vechi: {fmtMii(d.doar_erp_vechi)}.
    </div>
  );
}

function Card({ title, testid, children }) {
  return (
    <div data-testid={testid} className="rounded border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-2 text-sm font-medium text-slate-700">{title}</div>
      {children}
    </div>
  );
}

function Grafice({ data, toti, testid }) {
  const L = data.labels || [];
  const s = data.series || {};
  return (
    <div className="mb-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card title="Comenzi pe perioada" testid={`${testid}-chart-comenzi`}><EvolutieBarChart labels={L} values={s.total || []} /></Card>
      <Card title="Valoare EUR fara TVA pe perioada" testid={`${testid}-chart-valoare`}><EvolutieBarChart labels={L} values={s.val_total || []} /></Card>
      {toti && (
        <Card title="PF vs PJ (valoare EUR fara TVA)" testid={`${testid}-chart-pf-pj`}>
          <div className="text-xs text-blue-600">Persoana fizica</div>
          <EvolutieBarChart labels={L} values={s.val_pf || []} />
          <div className="mt-2 text-xs text-amber-700">Persoana juridica</div>
          <EvolutieBarChart labels={L} values={s.val_pj || []} />
        </Card>
      )}
      <Card title="Valoare cumulata EUR fara TVA" testid={`${testid}-chart-val-cumulat`}><EvolutieBarChart labels={L} values={s.val_cumulat || []} /></Card>
    </div>
  );
}

const COLS = [["pf", "PF"], ["pj", "PJ"], ["nedefinit", "Nedefinit"], ["total", "Total"], ["cumulat", "Cumulat"],
  ["val_pf", "EUR PF"], ["val_pj", "EUR PJ"], ["val_nedefinit", "EUR Nedefinit"], ["val_total", "EUR Total"], ["val_cumulat", "EUR Cumulat"]];

function Tabel({ rows, totals, testid }) {
  return (
    <div className="max-h-[480px] overflow-auto rounded border border-slate-200 bg-white shadow-sm">
      <table data-testid={`${testid}-table`} className="w-full text-sm">
        <thead className="sticky top-0 bg-slate-50 text-left text-slate-600">
          <tr><th className="px-3 py-2">Perioada</th>{COLS.map(([k, l]) => <th key={k} className="px-3 py-2 text-right">{l}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.start} className="border-t border-slate-100">
              <td className="px-3 py-1.5">{r.perioada}</td>
              {COLS.map(([k]) => <td key={k} className="px-3 py-1.5 text-right tabular-nums">{fmt(r[k])}</td>)}
            </tr>
          ))}
          <tr data-testid={`${testid}-table-total`} className="border-t-2 border-slate-300 font-semibold">
            <td className="px-3 py-2">Total</td>
            {COLS.map(([k]) => <td key={k} className="px-3 py-2 text-right tabular-nums">{k.endsWith("cumulat") ? "" : fmt(totals[k])}</td>)}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
